import { createContext, useContext, useEffect, useState, useCallback } from "react";
import api from "./api";

const AuthCtx = createContext(null);

function readJSON(key) {
  const raw = localStorage.getItem(key);
  try { return raw ? JSON.parse(raw) : null; } catch { return null; }
}

export function AuthProvider({ children }) {
  const [user, setUser] = useState(() => readJSON("sigsaop_user"));
  const [impersonating, setImpersonating] = useState(() => readJSON("sigsaop_impersonating"));
  const [loading, setLoading] = useState(false);

  const refreshSession = useCallback(async () => {
    const { data } = await api.get("/auth/session");
    localStorage.setItem("sigsaop_user", JSON.stringify(data.user));
    setUser(data.user);
    if (data.impersonating) {
      localStorage.setItem("sigsaop_impersonating", JSON.stringify(data.impersonating));
      setImpersonating(data.impersonating);
    } else {
      localStorage.removeItem("sigsaop_impersonating");
      setImpersonating(null);
    }
    return data;
  }, []);

  const login = useCallback(async (email, password) => {
    setLoading(true);
    try {
      const { data } = await api.post("/auth/login", { email, password });
      localStorage.setItem("sigsaop_token", data.token);
      localStorage.setItem("sigsaop_user", JSON.stringify(data.user));
      localStorage.removeItem("sigsaop_impersonating");
      setUser(data.user);
      setImpersonating(null);
      return data.user;
    } finally {
      setLoading(false);
    }
  }, []);

  const register = useCallback(async (payload) => {
    setLoading(true);
    try {
      const { data } = await api.post("/auth/register", payload);
      localStorage.setItem("sigsaop_token", data.token);
      localStorage.setItem("sigsaop_user", JSON.stringify(data.user));
      setUser(data.user);
      return data.user;
    } finally {
      setLoading(false);
    }
  }, []);

  const logout = useCallback(() => {
    localStorage.removeItem("sigsaop_token");
    localStorage.removeItem("sigsaop_user");
    localStorage.removeItem("sigsaop_impersonating");
    setUser(null);
    setImpersonating(null);
  }, []);

  const impersonateClient = useCallback(async (clientId) => {
    const { data } = await api.post(`/admin/clients/${clientId}/impersonate`);
    localStorage.setItem("sigsaop_token", data.token);
    localStorage.setItem("sigsaop_impersonating", JSON.stringify(data.impersonating));
    setImpersonating(data.impersonating);
    return data.impersonating;
  }, []);

  const stopImpersonation = useCallback(async () => {
    const { data } = await api.post("/admin/impersonate/stop");
    localStorage.setItem("sigsaop_token", data.token);
    localStorage.removeItem("sigsaop_impersonating");
    setImpersonating(null);
  }, []);

  useEffect(() => {
    const token = localStorage.getItem("sigsaop_token");
    if (token) {
      refreshSession().catch(() => logout());
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <AuthCtx.Provider value={{
      user, impersonating, login, register, logout, loading,
      impersonateClient, stopImpersonation, refreshSession,
    }}>
      {children}
    </AuthCtx.Provider>
  );
}

export const useAuth = () => useContext(AuthCtx);
